import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Title,
  Modal,
  Grid,
  Text,
  TypographyStylesProvider,
} from "@mantine/core";
import entityIcon from "@util/entityIcon";
import styled from "styled-components";
import { Carousel } from "@mantine/carousel";
import {
  IconPhoto,
  IconChevronLeft,
  IconChevronRight,
  IconHandFinger,
} from "@tabler/icons-react";
import dayjs from "dayjs";

const formatDateRange = (startDate, endDate, timezone) => {
  if (!startDate || !endDate) return "";

  const start = dayjs(startDate).tz(timezone);
  const end = dayjs(endDate).tz(timezone);

  // If same month and year
  if (start.format("MMM YYYY") === end.format("MMM YYYY")) {
    return `${start.format("MMM D")}-${end.format("D, YYYY")}`;
  }

  // If same year
  if (start.format("YYYY") === end.format("YYYY")) {
    return `${start.format("MMM D")}-${end.format("MMM D, YYYY")}`;
  }

  // Different years
  return `${start.format("MMM D, YYYY")}-${end.format("MMM D, YYYY")}`;
};

export default function GalleryEffortList({ items = [] }) {
  const [variant, setVariant] = useState(2);

  if (!items.length)
    return (
      <Text align="center" fw={600}>
        Check back soon for content!
      </Text>
    );

  return (
    <>
      {/* <Flex justify="center" gap="xl" mb="xl">
        {[1, 2, 3].map((m) => (
          <Button
            key={m}
            onClick={() => setVariant(m)}
            variant={variant === m ? "filled" : "outline"}
          >
            {m}
          </Button>
        ))}
      </Flex> */}
      <Grid
        gap="xl"
        display={{ base: variant === 1 ? "grid" : "none", md: "grid" }}
        justify="center"
      >
        {items.map((item, i) => (
          <Grid.Col span={{ base: 12, md: 4 }} mb="sm" key={item.id}>
            <Item
              variant={variant}
              badgeText={`${i + 1} of ${items.length}`}
              image={item.featured_image_url}
              title={item.title}
              dates={getDateString(
                item.gallery_start_date_formatted,
                item.gallery_end_date_formatted
              )}
              // dates={formatDateRange(
              //   item.start_date,
              //   item.end_date,
              //   item.timezone
              // )}
              // url={
              //   item.variety === 6
              //     ? `/ride-challenge-entry/${item.url_code}`
              //     : `/entry/${item.url_code}`
              // }
              url={`/entry/${item.url_code}`}
              description={item.description}
            />
          </Grid.Col>
        ))}
      </Grid>
      <Grid display={{ base: "grid", md: "none" }} gap="xl">
        {items.map((item, i) => (
          <Grid.Col span={12} key={item.id}>
            <Item2
              variant={variant}
              badgeText={`${i + 1} of ${items.length}`}
              image={item.featured_image_url}
              title={item.title}
              dates={getDateString(
                item.gallery_start_date_formatted,
                item.gallery_end_date_formatted
              )}
              // dates={formatDateRange(
              //   item.start_date,
              //   item.end_date,
              //   item.timezone
              // )}
              // url={
              //   item.variety === 6
              //     ? `/ride-challenge-entry/${item.url_code}`
              //     : `/entry/${item.url_code}`
              // }
              url={`/entry/${item.url_code}`}
              description={item.description}
            />
          </Grid.Col>
        ))}
      </Grid>
    </>
  );
}

const StyledItem = styled.div`
  position: relative;

  img {
    max-height: 350px;
    width: 100%;
    object-fit: cover;
    border-radius: 4px;
  }

  .mantine-Badge-root {
    position: absolute;
    top: 10px;
    right: 10px;
  }
`;

const Item = ({
  image,
  badgeText,
  title,
  dates,
  url,
  description,
  variant,
}) => {
  const [isOpen, setOpen] = useState(false);

  return (
    <div>
      <Modal opened={isOpen} onClose={() => setOpen(false)}>
        <Title mb="md" order={4} align="center">
          {title}
        </Title>
        <LinkButton url={url} />
        <Divider mt="md" mb="md" />
        {description && (
          <TypographyStylesProvider
            style={{
              paddingLeft: "0",
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </TypographyStylesProvider>
        )}
      </Modal>
      <StyledItem>
        <Card
          style={{
            overflow: "hidden",
            [`--card-padding`]: 0,
          }}
        >
          <Badge>{badgeText}</Badge>
          <Card.Section>
            {image ? (
              <img
                src={image}
                style={{
                  height: "250px",
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "center center",
                }}
              />
            ) : (
              <div
                style={{
                  height: "250px",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconPhoto />
              </div>
            )}
          </Card.Section>
          <Flex mt={7} gap="lg" direction={"column"}>
            <Box>
              <Text
                style={{ lineHeight: "1.2" }}
                fw={600}
                size="lg"
                mb="xs"
                align="center"
              >
                {title}
              </Text>
              <Flex mb="sm" align="center" gap="xs" justify="center">
                {/* {entityIcon.calendar()} */}
                <Text size={"9px"} c="dimmed" align="center">
                  {dates}
                </Text>
              </Flex>
              <Divider mt="sm" mb="sm" />
              <Flex align="center" gap="xs">
                <Button
                  onClick={() => setOpen(true)}
                  variant="light"
                  fullWidth
                  size="xs"
                  p={0}
                >
                  Read More
                </Button>
                <LinkButton url={url} />
              </Flex>
            </Box>
          </Flex>
        </Card>
      </StyledItem>
    </div>
  );
};

const StyledItem2 = styled.div`
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 12px;
  overflow: hidden;
  background: var(--mantine-color-dark-6);
  padding: 0;
  border: 2px solid var(--mantine-color-dark-5);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: var(--mantine-color-dark-4);
  }

  img {
    width: 100px;
    object-fit: cover;
    /* height: 100%; */
    height: 100px;
    border-radius: 8px 0 0 8px;
    transition: transform 0.2s ease;
    display: block;
  }

  &:hover img {
    transform: scale(1.05);
  }

  .mantine-Badge-root {
    position: absolute;
    top: 10px;
    right: 10px;
  }
`;

const Item2 = ({
  image,
  badgeText,
  title,
  dates,
  url,
  description,
  variant,
}) => {
  const [isOpen, setOpen] = useState(false);

  return (
    <div>
      <Modal opened={isOpen} onClose={() => setOpen(false)}>
        <Title mb="md" order={4} align="center">
          {title}
        </Title>
        <LinkButton url={url} />
        <Divider mt="md" mb="md" />
        {description && (
          <TypographyStylesProvider
            style={{
              paddingLeft: "0",
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </TypographyStylesProvider>
        )}
      </Modal>
      <StyledItem2 onClick={() => setOpen(true)}>
        <Box>
          <Flex gap={0} align="stretch">
            <Box style={{ padding: 0 }}>
              <img src={image} />
            </Box>
            <Box style={{ flex: 1, padding: "12px 12px 12px 12px" }}>
              <Text c="white" fw={600} size="md" mb={4}>
                {title}
              </Text>
              <Text size="sm" c="dimmed">
                {dates}
              </Text>
            </Box>
          </Flex>
        </Box>
      </StyledItem2>
    </div>
  );
};

function getDateString(startDate, endDate) {
  if (!startDate || !endDate) return "";

  const startParts = startDate.split(" ");
  const endParts = endDate.split(" ");

  if (
    startParts.length >= 3 &&
    endParts.length >= 3 &&
    startParts[startParts.length - 1] === endParts[endParts.length - 1]
  ) {
    const startWithoutYear = startParts
      .slice(0, -1)
      .join(" ")
      .replace(/,\s*$/, "");
    return `${startWithoutYear}-${endDate}`;
  }

  return `${startDate}-${endDate}`;
}

const LinkButton = ({ url }) => (
  <Button
    component="a"
    href={url}
    target="_blank"
    fullWidth
    size="sm"
    // variant="light"
  >
    Enter
  </Button>
);
